import React from 'react';
import {
  useRpcMediaAnyCoarsePointer,
  useRpcMediaSmallScreen,
  type useMatchMediaReturnType,
} from '../components/hooks/useMatchMedia/useRpcMatchMedia';

export type UiMediaQueryContextValue = Readonly<{
  /**
   * True if the screen is smaller than the small viewport.
   *
   * If you are using this to set CSS properties, you probably want to use media queries instead: `media.down($['viewport.small'])`
   */
  isSmallScreen?: useMatchMediaReturnType;
  isTouch?: useMatchMediaReturnType;
}>;

const UiMediaQueryContext = React.createContext<UiMediaQueryContextValue>({});

export const useMediaQuery = (): UiMediaQueryContextValue => {
  return React.useContext(UiMediaQueryContext);
};

export const UiMediaQueryProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({children}) => {
  /**
   * We initialize these hooks in in a top-level provider instead of in each specific component
   * so that their value is computed as soon as possible when the applications is rendered.
   * Otherwise, animated components such as Dialog or Drawer wouldn't be animated
   * properly on the first render.
   */
  const isSmallScreen = useRpcMediaSmallScreen();
  const isTouch = useRpcMediaAnyCoarsePointer();

  const context = React.useMemo(
    () => ({
      isSmallScreen,
      isTouch,
    }),
    [isSmallScreen, isTouch],
  );

  return (
    <UiMediaQueryContext.Provider value={context}>
      {children}
    </UiMediaQueryContext.Provider>
  );
};
